/**
 * D-EZ-102 / D-EZ-95 — THE CHAMPION'S MOMENT AND THE YEAR IN REVIEW.
 *
 * When a season settles: the trophy (the champion, the final four, the race
 * that decided it) and then the year in review — the awards, the records that
 * fell, who retired and who goes into the Hall of Fame — before the winter's
 * numbers. Every line is a fold over the settled seasons (`history/`), so the
 * review can never disagree with the record book.
 */

import {
  buildRegistry,
  careerRecords,
  hallOfFame,
  seasonAwards,
  seasonRecords,
  sortedSeasons,
  type Award,
  type DriverRegistry,
  type HallOfFamer,
  type HistorySeason,
  type LeagueRecord,
} from '../history/index.ts';
import { dollars } from '../paper/fallbackPaper.ts';
import { hometownFor } from './hometown.ts';
import { ordinal } from './raceStory.ts';
import { seasonArc, type SeasonArc } from '../chapters/arc.ts';

export interface FinalFourLine {
  driverId: string;
  name: string;
  /** Where he finished the title-deciding race; null if he did not start it. */
  finish: number | null;
  champion: boolean;
}

export interface RecordBroken {
  title: string;
  scope: 'season' | 'career';
  driverId: string;
  name: string;
  value: number;
  /** Who held it before this season, and at what. */
  previous: { name: string; value: number; seasonNumber?: number } | null;
  /** Ready-to-print sentence. */
  text: string;
}

export interface RetirementLine {
  driverId: string;
  name: string;
  age: number | null;
  seasons: number;
  wins: number;
  championships: number;
  starts: number;
}

export interface DepartureLine {
  driverId: string;
  name: string;
  raceNumber: number;
  text: string;
}

export interface YearInReview {
  seasonNumber: number;
  racesRun: number;
  differentWinners: number;
  champion: {
    driverId: string;
    name: string;
    teamName: string | null;
    points: number;
    wins: number;
    /** Career titles, this one included. */
    titles: number;
    hometown: string | null;
  } | null;
  finalFour: FinalFourLine[];
  decider: { raceNumber: number; raceName: string; trackName: string; winnerName: string | null; text: string } | null;
  awards: Award[];
  recordsBroken: RecordBroken[];
  retirements: RetirementLine[];
  /** Drivers lost in the season, if any. Told plainly and respectfully. */
  lost: DepartureLine[];
  /** The Hall of Fame class this season's end produces (inducted next season). */
  hallOfFame: HallOfFamer[];
  /**
   * D-EZ-110 — the year as a story: its chapter markers (the opener, halfway,
   * the cut, the final four, the finale), how it began, where it turned, how it
   * ended, and the rivalries that ran through it. Null when not asked for.
   */
  arc: SeasonArc | null;
}

const HEADLINE_AWARDS = ['champion', 'rookie-of-the-year', 'most-popular', 'most-improved'];

export function yearInReview(
  input: readonly HistorySeason[],
  seasonNumber: number,
  opts: { arc?: boolean } = {},
): YearInReview | null {
  const seasons = sortedSeasons(input).filter((s) => s.seasonNumber <= seasonNumber);
  const s = seasons.find((x) => x.seasonNumber === seasonNumber);
  if (!s || s.complete === false) return null;
  const registry = buildRegistry(seasons);
  const standings = s.season.finalStandings;
  const teamName = (teamId: string | null | undefined) => s.teams.find((t) => t.teamId === teamId)?.name ?? null;

  const champId = s.season.championId;
  const champStat = champId ? standings.byDriver[champId] : undefined;
  const champion =
    champId && champStat
      ? {
          driverId: champId,
          name: champStat.name,
          teamName: teamName(champStat.teamId ?? null),
          points: champStat.points,
          wins: champStat.wins,
          titles: registry.byId[champId]?.career.championships ?? 1,
          hometown: hometownFor(champId),
        }
      : null;

  const races = s.season.races;
  const last = races[races.length - 1] ?? null;
  const finishIn = (id: string): number | null => last?.race.finishingOrder.find((f) => f.driverId === id)?.place ?? null;
  const finalFour: FinalFourLine[] = (s.season.playoff?.finalFour ?? []).map((id) => ({
    driverId: id,
    name: standings.byDriver[id]?.name ?? registry.byId[id]?.name ?? id,
    finish: finishIn(id),
    champion: id === champId,
  }));
  finalFour.sort((a, b) => Number(b.champion) - Number(a.champion) || (a.finish ?? 99) - (b.finish ?? 99));

  let decider: YearInReview['decider'] = null;
  if (last) {
    const winner = last.race.finishingOrder.find((f) => f.driverId === last.race.winnerId) ?? null;
    const champFinish = champId ? finishIn(champId) : null;
    const text =
      champion && champFinish !== null
        ? champFinish === 1
          ? `${champion.name} won the ${last.scheduled.raceName} to take the title.`
          : `${champion.name} finished ${ordinal(champFinish)} in the ${last.scheduled.raceName}${winner ? `, won by ${winner.name},` : ''} and that was enough for the title.`
        : `${winner ? `${winner.name} won` : 'Nobody won'} the ${last.scheduled.raceName}, the last race of the year.`;
    decider = {
      raceNumber: last.raceNumber,
      raceName: last.scheduled.raceName,
      trackName: last.scheduled.trackName,
      winnerName: winner?.name ?? null,
      text,
    };
  }

  const awards = seasonAwards(seasons, seasonNumber, registry);
  awards.sort((a, b) => rankAward(a.kind) - rankAward(b.kind));

  const retirements: RetirementLine[] = (s.offseason?.retirements ?? []).map((r) => {
    const e = registry.byId[r.driverId];
    return {
      driverId: r.driverId,
      name: e?.name ?? r.driverId,
      age: r.age ?? e?.age ?? null,
      seasons: e?.career.seasons ?? 0,
      wins: e?.career.wins ?? 0,
      championships: e?.career.championships ?? 0,
      starts: e?.career.starts ?? 0,
    };
  });
  retirements.sort((a, b) => b.wins - a.wins || b.starts - a.starts || a.name.localeCompare(b.name));

  const lost: DepartureLine[] = (s.season.fatalities ?? []).map((f) => ({
    driverId: f.driverId,
    name: f.name,
    raceNumber: f.raceNumber,
    text: `${f.name} lost his life in race ${f.raceNumber}.`,
  }));

  // The class this winter produces: everyone who left the sport at the end of
  // this season (inducted next season), and anyone lost during it (at once).
  const nextClass = s.offseason
    ? hallOfFame(registry, seasonNumber + 1).filter((h) =>
        registry.byId[h.driverId]?.status === 'died' ? h.inductedSeason === seasonNumber : h.inductedSeason === seasonNumber + 1,
      )
    : [];

  return {
    seasonNumber,
    racesRun: races.length,
    differentWinners: new Set(races.map((r) => r.race.winnerId).filter(Boolean)).size,
    champion,
    finalFour,
    decider,
    awards,
    recordsBroken: recordsBrokenIn(seasons, seasonNumber),
    retirements,
    lost,
    hallOfFame: nextClass,
    arc: opts.arc === false ? null : arcOf(seasons, seasonNumber),
  };
}

/** The season's arc; a season too thin to tell (a test fixture, a stripped save) has none. */
function arcOf(seasons: readonly HistorySeason[], seasonNumber: number): SeasonArc | null {
  try {
    return seasonArc(seasons, seasonNumber);
  } catch {
    return null;
  }
}

function rankAward(kind: string): number {
  const i = HEADLINE_AWARDS.indexOf(kind);
  return i === -1 ? HEADLINE_AWARDS.length : i;
}

/**
 * RECORDS THAT FELL THIS SEASON — a record held by somebody before this season
 * and taken by somebody else (or pushed past by a single season) in it.
 *
 * A league's first season breaks nothing: there was no record to break, and a
 * screen that called every first-year number a record would be crying wolf.
 */
export function recordsBrokenIn(input: readonly HistorySeason[], seasonNumber: number): RecordBroken[] {
  const seasons = sortedSeasons(input).filter((s) => s.seasonNumber <= seasonNumber && s.complete !== false);
  const before = seasons.filter((s) => s.seasonNumber < seasonNumber);
  if (before.length === 0 || !seasons.some((s) => s.seasonNumber === seasonNumber)) return [];
  const regBefore = buildRegistry(before);
  const regNow = buildRegistry(seasons);
  const out: RecordBroken[] = [];

  const compare = (was: LeagueRecord[], now: LeagueRecord[], scope: 'season' | 'career') => {
    for (const rec of now) {
      const prev = was.find((r) => r.stat === rec.stat);
      const top = rec.holders[0];
      const old = prev?.holders[0];
      if (!top || !old) continue;
      if (top.value <= old.value) continue;
      if (scope === 'season' && top.seasonNumber !== seasonNumber) continue;
      if (scope === 'career' && rec.holders.some((h) => prev?.holders.some((p) => p.driverId === h.driverId))) continue;
      const shown = rec.stat === 'winningsMb' ? money(top.value) : String(top.value);
      const oldShown = rec.stat === 'winningsMb' ? money(old.value) : String(old.value);
      out.push({
        title: rec.title,
        scope,
        driverId: top.driverId,
        name: top.name,
        value: top.value,
        previous: { name: old.name, value: old.value, seasonNumber: old.seasonNumber },
        text:
          scope === 'season'
            ? `${rec.title}: ${top.name}, ${shown} — beating ${old.name}'s ${oldShown}${old.seasonNumber ? ` from season ${old.seasonNumber}` : ''}.`
            : `${rec.title}: ${top.name} passes ${old.name}, now at ${shown}.`,
      });
    }
  };
  compare(seasonRecords(regBefore), seasonRecords(regNow), 'season');
  compare(careerRecords(regBefore), careerRecords(regNow), 'career');
  return out;
}

/** Megabucks to the figure a newspaper prints — through `money.ts`, never a constant here. */
const money = dollars;

/** Every retired (or otherwise departed) driver, most recent first. */
export function formerDrivers(registry: DriverRegistry) {
  return registry.former;
}
